import React, { useState, useEffect, useRef } from 'react';
import './grideditor.css';
import NavBar from '../../Components/NavBar/navbar';
import FooterSection from '../../Components/Footer/footer';
import GridMapEditor from '../../Components/Grid/GridMapEditor';
import PathBoard from '../../Components/Grid/PathBoard';
import { API_ENDPOINTS } from '../../utils/config';
import { THEME, pageBackgroundSx, gridOverlaySx } from '../../theme';
import {
    Grid,
    Typography,
    Box,
    Button,
    Alert,
    Tabs,
    Tab,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Slider,
    Chip,
    Stack,
    IconButton,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TuneIcon from '@mui/icons-material/Tune';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const panelSx = {
    bgcolor: THEME.cardBg,
    border: `1px solid ${THEME.border}`,
    borderRadius: 2,
    p: 2,
};

const selectSx = {
    color: THEME.textMain,
    bgcolor: '#121214',
    '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.primary },
    '.MuiSvgIcon-root': { color: THEME.secondary },
};

const Grideditor = () => {
    const [modelType, setModelType] = useState('Hybrid-standard');
    const [inputGridShape, setInputGridShape] = useState({ rows: 20, cols: 20 });
    const [gridSize, setGridSize] = useState(20);
    const [obstacleDensity, setObstacleDensity] = useState(0.1);
    const [numAgents, setNumAgents] = useState(5);
    const [actionFilter, setActionFilter] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [modelInfo, setModelInfo] = useState(null);
    const [results, setResults] = useState(null);
    const [solvedGrid, setSolvedGrid] = useState(null);
    const [paths, setPaths] = useState(null);
    const [startPositions, setStartPositions] = useState([]);
    const [goalPositions, setGoalPositions] = useState([]);

    const [currentFrame, setCurrentFrame] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const animationRef = useRef(null);

    const modelOptions = [
        { value: 'RL', label: 'Reinforcement Learning' },
        { value: 'IL', label: 'Imitation Learning' },
        { value: 'Hybrid-standard', label: 'Hybrid Standard' },
        { value: 'Hybrid-forced', label: 'Hybrid Forced' },
        { value: 'PRIMAL_original', label: 'PRIMAL Original' },
    ];

    const API_BASE = API_ENDPOINTS.MOBILITY_SYSTEMS;

    const pauseAnimation = () => {
        setIsPlaying(false);
        if (animationRef.current) {
            clearInterval(animationRef.current);
            animationRef.current = null;
        }
    };

    const startAnimation = () => {
        if (!paths?.length) return;
        pauseAnimation();
        setIsPlaying(true);
        animationRef.current = setInterval(() => {
            setCurrentFrame((prev) => {
                const next = prev + 1;
                if (next >= paths.length) {
                    pauseAnimation();
                    return paths.length - 1;
                }
                return next;
            });
        }, 220);
    };

    const resetAnimation = () => {
        pauseAnimation();
        setCurrentFrame(0);
    };

    useEffect(() => () => pauseAnimation(), []);

    const applySolveResult = (data) => {
        const grid = data.solved_grid || data.grid || null;
        const nextPaths = data.paths || null;
        setSolvedGrid(grid);
        setPaths(nextPaths);
        setStartPositions(data.start_positions || nextPaths?.[0] || []);
        setGoalPositions(
            data.goal_positions || (nextPaths?.length ? nextPaths[nextPaths.length - 1] : [])
        );
        setResults(data);
        setCurrentFrame(0);
        pauseAnimation();
        if (nextPaths?.length) {
            // Play the animation after a successful solve
            setTimeout(() => {
                setIsPlaying(true);
                animationRef.current = setInterval(() => {
                    setCurrentFrame((prev) => {
                        const next = prev + 1;
                        if (next >= nextPaths.length) {
                            if (animationRef.current) clearInterval(animationRef.current);
                            setIsPlaying(false);
                            return nextPaths.length - 1;
                        }
                        return next;
                    });
                }, 220);
            }, 100);
        }
    };

    const handleGridSolve = async (gridMap) => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const rows = gridMap?.length || 0;
            const cols = gridMap?.[0]?.length || 0;
            const flat = gridMap.flat();
            const agents = Math.max(1, flat.filter((c) => c > 100 && c < 499).length);
            setInputGridShape({ rows, cols });

            // Always solve the painted grid with Hybrid-standard
            const response = await fetch(`${API_BASE}/solve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    model_type: 'Hybrid-standard',
                    grid_size: Math.max(rows, cols),
                    obstacle_density: 0.0,
                    num_agents: agents,
                    action_filter: false,
                    custom_grid: gridMap,
                }),
            });
            const data = await response.json();
            if (response.ok && (data.solved_grid || data.paths)) {
                applySolveResult(data);
                setSuccess('Path found.');
            } else {
                console.error('Grid solve failed:', data.error || data);
                setError('Could not solve this grid. Try again.');
            }
        } catch (err) {
            console.error(err);
            setError("Server not responding. Try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (_e, newValue) => {
        setTabValue(newValue);
        setError(null);
        setSuccess(null);
        pauseAnimation();
    };

    const checkHealth = async () => {
        try {
            const response = await fetch(`${API_BASE}/health`);
            const data = await response.json();
            return data;
        } catch {
            setError(
                'Could not reach the mobility services.'
            );
            return null;
        }
    };

    const getModelInfo = async () => {
        try {
            const response = await fetch(`${API_BASE}/model-info`);
            const data = await response.json();
            setModelInfo(data);
        } catch (err) {
            console.error(err);
        }
    };

    const runInference = async (customGrid = null) => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        setResults(null);
        setSolvedGrid(null);
        setPaths(null);
        resetAnimation();

        try {
            const health = await checkHealth();
            if (!health) {
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_BASE}/solve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    model_type: modelType,
                    grid_size: gridSize,
                    obstacle_density: obstacleDensity,
                    num_agents: numAgents,
                    action_filter: actionFilter,
                    custom_grid: customGrid,
                }),
            });
            const data = await response.json();
            if (response.ok && data.success !== false) {
                applySolveResult(data);
                setSuccess('Done.');
                setTabValue(1);
            } else {
                console.error('Planning failed:', data.error || data);
                setError('Planning failed. Try again.');
            }
        } catch (err) {
            console.error(err);
            setError("Couldn't run planning. Try again later.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getModelInfo();
        checkHealth();
        // Mount-only health/model probe; wrapping would re-fetch every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const metrics = results?.metrics || {};

    return (
        <Box sx={{ ...pageBackgroundSx, color: THEME.textMain }}>
            <Box sx={gridOverlaySx} />
            <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
                <NavBar />

                <Box sx={{ pt: 12, px: { xs: 2, md: 4 }, pb: 2, textAlign: 'center' }}>
                    <Typography
                        variant="overline"
                        sx={{ color: THEME.primary, fontWeight: 700, letterSpacing: 2 }}
                    >
                        EVOLVING SYSTEMS / ROBOTIC
                    </Typography>
                    <Typography variant="h3" fontWeight={800} mb={1}>
                        Mobility Systems
                    </Typography>
                    <Typography
                        variant="body1"
                        color={THEME.secondary}
                        sx={{ maxWidth: 720, mx: 'auto', mb: 2 }}
                    >
                        Plan paths for multiple robots through a shared grid — paint a map, or
                        generate one and watch them navigate.
                    </Typography>
                    {modelInfo && (
                        <Chip
                            size="small"
                            label={`Models: ${Object.entries(modelInfo.models || {})
                                .filter(([, v]) => v)
                                .map(([k]) => k)
                                .join(', ') || 'none'}`}
                            sx={{
                                bgcolor: THEME.cardBg,
                                color: THEME.secondary,
                                border: `1px solid ${THEME.border}`,
                            }}
                        />
                    )}
                </Box>

                <Box sx={{ px: { xs: 2, md: 4 }, mb: 2 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        centered
                        textColor="inherit"
                        TabIndicatorProps={{ style: { backgroundColor: THEME.primary } }}
                    >
                        <Tab
                            label="Environment Editor"
                            icon={<TuneIcon />}
                            iconPosition="start"
                            sx={{
                                color: THEME.secondary,
                                '&.Mui-selected': { color: THEME.primary },
                                textTransform: 'none',
                            }}
                        />
                        <Tab
                            label="Robot Navigation"
                            icon={<SmartToyIcon />}
                            iconPosition="start"
                            sx={{
                                color: THEME.secondary,
                                '&.Mui-selected': { color: THEME.primary },
                                textTransform: 'none',
                            }}
                        />
                    </Tabs>
                </Box>

                {(error || success) && (
                    <Box sx={{ px: { xs: 2, md: 4 }, mb: 2 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {success && <Alert severity="success">{success}</Alert>}
                    </Box>
                )}

                <Box sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                    {tabValue === 0 && (
                        <Grid container spacing={2} alignItems="stretch">
                            <Grid item xs={12} md={6}>
                                <Box sx={{ ...panelSx, height: '100%' }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ color: THEME.primary, mb: 1, letterSpacing: 1 }}
                                    >
                                        <b>Map</b>
                                   
                                    </Typography>
                                    <Typography variant="body2" color={THEME.secondary} sx={{ mb: 2 }}>
                                        Starts (green), goals (red), obstacles (glowing).
                                        For best results, a square grid is recommended.
                                        {/* Solve uses Hybrid-standard with the exact painted layout. */}
                                    </Typography>
                                    <GridMapEditor
                                        onSolve={runInference}
                                        onGridSolve={handleGridSolve}
                                        solvedGrid={null}
                                        onShapeChange={(r, c) => setInputGridShape({ rows: r, cols: c })}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ ...panelSx, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 1.5,
                                            flexWrap: 'wrap',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ color: THEME.primary, letterSpacing: 1 }}
                                        >
                                            <b>Result</b> (view planned paths)
                                        </Typography>
                                        {paths?.length > 0 && (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={isPlaying ? pauseAnimation : startAnimation}
                                                    sx={{
                                                        color: THEME.primary,
                                                        border: `1px solid ${THEME.border}`,
                                                    }}
                                                >
                                                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={resetAnimation}
                                                    sx={{
                                                        color: THEME.secondary,
                                                        border: `1px solid ${THEME.border}`,
                                                    }}
                                                >
                                                    <RestartAltIcon />
                                                </IconButton>
                                                <Typography variant="caption" color={THEME.secondary}>
                                                    {currentFrame + 1} / {paths.length}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                    {solvedGrid && paths?.length ? (
                                        <Box sx={{ flex: 1, minHeight: 500 }}>
                                            <PathBoard
                                                grid={solvedGrid}
                                                paths={paths}
                                                startPositions={startPositions}
                                                goalPositions={goalPositions}
                                                step={currentFrame}
                                                height={500}
                                                title={`${inputGridShape.rows}×${inputGridShape.cols}`}
                                            />
                                            <Slider
                                                size="small"
                                                value={currentFrame}
                                                min={0}
                                                max={Math.max(paths.length - 1, 0)}
                                                onChange={(_e, v) => {
                                                    pauseAnimation();
                                                    setCurrentFrame(v);
                                                }}
                                                sx={{ color: THEME.primary, mt: 1.5 }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minHeight: 500,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                textAlign: 'center',
                                                color: THEME.secondary,
                                                border: `1px dashed ${THEME.border}`,
                                                borderRadius: 2,
                                                px: 2,
                                            }}
                                        >
                                            Solve the grid to watch the robots move.
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {tabValue === 1 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <Box sx={panelSx}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ color: THEME.primary, mb: 2, letterSpacing: 1 }}
                                    >
                                        Settings
                                    </Typography>

                                    <Typography variant="caption" color={THEME.secondary}>
                                        Model
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{ mb: 2, mt: 0.5 }}>
                                        <Select
                                            value={modelType}
                                            onChange={(e) => setModelType(e.target.value)}
                                            sx={selectSx}
                                        >
                                            {modelOptions.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Typography variant="caption" color={THEME.secondary}>
                                        Grid size
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{ mb: 2, mt: 0.5 }}>
                                        <Select
                                            value={gridSize}
                                            onChange={(e) => setGridSize(e.target.value)}
                                            sx={selectSx}
                                        >
                                            {[10, 20, 30].map((size) => (
                                                <MenuItem key={size} value={size}>
                                                    {size} × {size}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Typography variant="caption" color={THEME.secondary}>
                                        Obstacle density
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{ mb: 2, mt: 0.5 }}>
                                        <Select
                                            value={obstacleDensity}
                                            onChange={(e) => setObstacleDensity(e.target.value)}
                                            sx={selectSx}
                                        >
                                            {[0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3].map((d) => (
                                                <MenuItem key={d} value={d}>
                                                    {(d * 100).toFixed(0)}%
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Typography variant="caption" color={THEME.secondary}>
                                        Agents: {numAgents}
                                    </Typography>
                                    <Slider
                                        value={numAgents}
                                        onChange={(_e, v) => setNumAgents(v)}
                                        min={2}
                                        max={10}
                                        size="small"
                                        valueLabelDisplay="auto"
                                        sx={{ color: THEME.primary, mb: 1, mt: 1 }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={actionFilter}
                                                onChange={(e) => setActionFilter(e.target.checked)}
                                                sx={{
                                                    color: THEME.secondary,
                                                    '&.Mui-checked': { color: THEME.primary },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" color={THEME.secondary}>
                                                Action filter
                                            </Typography>
                                        }
                                        sx={{ mb: 2 }}
                                    />

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={
                                            loading ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <PlayArrowIcon />
                                            )
                                        }
                                        onClick={() => runInference()}
                                        disabled={loading}
                                        sx={{
                                            bgcolor: THEME.primary,
                                            color: '#000',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            py: 1.2,
                                            '&:hover': { bgcolor: THEME.primaryHover },
                                        }}
                                    >
                                        {loading ? 'Running…' : 'Run'}
                                    </Button>

                                    {results && (
                                        <Stack spacing={0.75} sx={{ mt: 2.5 }}>
                                            <Chip
                                                size="small"
                                                label={metrics.finished ? 'Reached goals' : 'Did not finish'}
                                                sx={{
                                                    bgcolor: metrics.finished
                                                        ? 'rgba(44,187,93,0.15)'
                                                        : 'rgba(239,71,67,0.15)',
                                                    color: metrics.finished
                                                        ? THEME.primary
                                                        : THEME.accent,
                                                    border: `1px solid ${
                                                        metrics.finished
                                                            ? THEME.primary
                                                            : THEME.accent
                                                    }`,
                                                }}
                                            />
                                            <Typography variant="body2" color={THEME.secondary}>
                                                Steps {metrics.steps ?? '—'} · Collisions{' '}
                                                {metrics.robot_collisions ?? 0}
                                            </Typography>
                                            <Typography variant="body2" color={THEME.secondary}>
                                                Inference{' '}
                                                {results.inference_time != null
                                                    ? `${results.inference_time.toFixed(2)}s`
                                                    : '—'}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={9}>
                                <Box sx={panelSx}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 1.5,
                                            flexWrap: 'wrap',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ color: THEME.primary, letterSpacing: 1 }}
                                        >
                                            Paths
                                        </Typography>
                                        {paths?.length > 0 && (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={isPlaying ? pauseAnimation : startAnimation}
                                                    sx={{
                                                        color: THEME.primary,
                                                        border: `1px solid ${THEME.border}`,
                                                    }}
                                                >
                                                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={resetAnimation}
                                                    sx={{
                                                        color: THEME.secondary,
                                                        border: `1px solid ${THEME.border}`,
                                                    }}
                                                >
                                                    <RestartAltIcon />
                                                </IconButton>
                                                <Typography variant="caption" color={THEME.secondary}>
                                                    {currentFrame + 1} / {paths.length}
                                                </Typography>
                                                <Slider
                                                    size="small"
                                                    value={currentFrame}
                                                    min={0}
                                                    max={Math.max(paths.length - 1, 0)}
                                                    onChange={(_e, v) => {
                                                        pauseAnimation();
                                                        setCurrentFrame(v);
                                                    }}
                                                    sx={{
                                                        width: 140,
                                                        color: THEME.primary,
                                                        ml: 1,
                                                    }}
                                                />
                                            </Stack>
                                        )}
                                    </Box>

                                    {solvedGrid && paths ? (
                                        <PathBoard
                                            grid={solvedGrid}
                                            paths={paths}
                                            startPositions={startPositions}
                                            goalPositions={goalPositions}
                                            step={currentFrame}
                                            height={580}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                height: 480,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexDirection: 'column',
                                                border: `1px dashed ${THEME.border}`,
                                                borderRadius: 2,
                                                color: THEME.secondary,
                                                gap: 1,
                                            }}
                                        >
                                            <Typography variant="body1">
                                                Pick settings on the left, then hit Run.
                                            </Typography>
                                            <Typography variant="body2">
                                                S marks starts, G marks goals, and arrows show each path.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Box>

                <FooterSection />
            </Box>
        </Box>
    );
};

export default Grideditor;
